import React, { useState } from 'react';
import { connect } from 'react-redux';

import { ticketActivitiesSet } from '~/actions/ticket';
import {
  downloadTicketDocument,
  getActivities,
  shareTicketDocument,
} from '~/api/tickets';
import airplaneSrc from '~/assets/images/cta/airplane.svg';
import downloadSrc from '~/assets/images/cta/download.svg';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { EntityType } from '~/enums/EntityType';
import { TicketStageType } from '~/enums/TicketStageType';
import { useAsync } from '~/hooks/useAsync';
import { FormattedMessage, useIntl } from 'react-intl';
import { getDocumentVersion } from '~/reducers/ticket';
import { useRouting } from '~/routing/useRouting';
import { DOWNLOAD, ERROR, SUCCESS } from '~/types/toast.types';
import CtaSection from '~/ui/components/CtaSection/CtaSection';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { coerceFileType, downloadFile, getBasename } from '~/utils/files';

import OutboundEmailModal from '../../../../Modals/EmailModal/OutboundEmailModal';

const ShareAndDownloadCta = ({
  nextStageString,
  ticketId,
  title,
  shouldShowShare,
  shouldShowDownload,
  stage,
  // connected
  ticket,
  ticketActivitiesSet,
  version,
}) => {
  const intl = useIntl();
  const { navigate } = useRouting();
  const { document } = ticket;
  const { id: documentId, name, type } = document;
  const versionId = version?.id;
  const attachments = type ? [{ name, type: coerceFileType(type || '') }] : [];
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState();

  const shareDescription = intl.formatMessage(
    {
      id: 'ShareAndDownloadCta.shareContractDescription',
      defaultMessage: 'Share the contract {beforeSubmitting}',
      description:
        'Description for sharing contract, with optional additional text for next stage',
    },
    {
      beforeSubmitting:
        stage !== TicketStageType.Review && nextStageString
          ? intl.formatMessage(
              {
                id: 'ShareAndDownloadCta.beforeSubmittingFor',
                defaultMessage: 'before submitting it for {nextStageString}',
                description:
                  'Additional text when there is a next stage. Example: nextStageString = "Review", "Signatures"',
              },
              { nextStageString },
            )
          : '',
    },
  );

  function handleShowModal() {
    setShowModal(true);
  }

  function handleHideModal() {
    setShowModal(false);
  }

  const { executor: handleOnShareSuccess } = useAsync(
    getActivities,
    { entityId: ticketId, entityType: EntityType.Ticket },
    {
      successHandler: ticketActivitiesSet,
    },
  );

  const actionButton = {
    text: intl.formatMessage({
      id: 'ShareAndDownloadCta.shareButton',
      defaultMessage: 'Share',
      description: 'Share button text',
    }),
    variant: 'primary',
    errorHandler: () =>
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'ShareAndDownloadCta.shareErrorMessage',
          defaultMessage: 'Unable to share document, please try again',
          description: 'Error message when document sharing fails',
        }),
      ),
    promise: async function () {
      if (data) {
        await shareTicketDocument({ data, documentId, versionId: versionId });
        handleOnShareSuccess();
        navigate(`/workflow/tickets/${ticketId}`);
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'ShareAndDownloadCta.shareSuccessMessage',
            defaultMessage: 'Document successfully shared.',
            description: 'Success message when document is shared',
          }),
        );
      }
    },
  };

  const { executor: handleOnDownloadClick } = useAsync(
    downloadTicketDocument,
    { documentId, versionId },
    {
      successToastMessage: intl.formatMessage(
        {
          id: 'ShareAndDownloadCta.downloadingMessage',
          defaultMessage: '"{ticketName}" is now downloading…',
          description: 'Message shown when document starts downloading',
        },
        { ticketName: ticket.name },
      ),
      successToastType: DOWNLOAD,
      successHandler: async (file) => {
        downloadFile(
          file,
          `${getBasename(document.name)}_${document.version}.${
            version.fileType
          }`,
        );
        const activities = await getActivities({
          entityId: ticket.id,
          entityType: EntityType.Ticket,
        });
        ticketActivitiesSet(activities);
      },
      errorToastMessage: intl.formatMessage({
        id: 'ShareAndDownloadCta.downloadErrorMessage',
        defaultMessage:
          'There has been an error with downloading this document. Please try again.',
        description: 'Error message when document download fails',
      }),
    },
  );

  if (!shouldShowShare) {
    return (
      <CtaSection
        actionButton={{
          disabled: !versionId,
          text: intl.formatMessage({
            id: 'ShareAndDownloadCta.downloadButton',
            defaultMessage: 'Download',
            description: 'Download button text',
          }),
          onClick: handleOnDownloadClick,
        }}
        description={
          <FlexLayout>
            <Text>
              {intl.formatMessage({
                id: 'ShareAndDownloadCta.downloadDescription',
                defaultMessage: 'Download the contract for use offline.',
                description: 'Description for download action',
              })}
            </Text>
          </FlexLayout>
        }
        imageSrc={downloadSrc}
      />
    );
  }

  return (
    <CtaSection
      actionButton={{
        text: intl.formatMessage({
          id: 'ShareAndDownloadCta.shareButton',
          defaultMessage: 'Share',
          description: 'Share button text',
        }),
        onClick: handleShowModal,
      }}
      title={title}
      description={
        <FlexLayout>
          <div>
            {shareDescription}
            {shouldShowDownload ? (
              <Text>
                {' '}
                <FormattedMessage
                  id="ShareAndDownloadCta.orDownloadText"
                  defaultMessage="or <downloadButton>Download</downloadButton> it for use offline"
                  description="Text with download option between share and download"
                  values={{
                    downloadButton: (chunks) => (
                      <Button
                        variant="action"
                        isInline
                        disabled={!versionId}
                        onClick={handleOnDownloadClick}
                        text={chunks}
                        PRIVATE_isPadded={false}
                      />
                    ),
                  }}
                />
              </Text>
            ) : null}
            .
          </div>
          <OutboundEmailModal
            actionButton={actionButton}
            attachments={attachments}
            title={intl.formatMessage({
              id: 'ShareAndDownloadCta.shareDocumentModalTitle',
              defaultMessage: 'Share Document',
              description: 'Title for share document modal',
            })}
            visible={showModal}
            onHide={handleHideModal}
            onUpdate={setData}
          />
        </FlexLayout>
      }
      imageSrc={airplaneSrc}
    />
  );
};

const mapStateToProps = ({ ticket }) => ({
  ticket,
  version: getDocumentVersion(ticket),
});

const mapDispatchToProps = { ticketActivitiesSet };

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ShareAndDownloadCta);
